import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import type { User } from 'oidc-client-ts';
import { apiService } from '../services/apiService';
import { setTokenProvider as setHttpTokenProvider } from '../services/http/httpClient';
import { apiClient } from '../services/apiClient';
import { ENV } from '../config/env';

const INVALID_REFRESH_TOKEN_MARKERS = [
  'invalid refresh token',
  'expired refresh token',
  'unknown, invalid, or expired refresh token'
];

const isInvalidRefreshTokenError = (error: unknown): boolean => {
  const text = (() => {
    if (!error) {return '';}
    if (typeof error === 'string') {return error;}
    if (error instanceof Error) {
      return `${error.name} ${error.message}`;
    }
    try {
      const serialized = JSON.stringify(error);
      if (serialized) {
        return serialized;
      }
    } catch {
      // Ignore serialization errors and continue with fallback text.
    }
    return 'unknown auth error';
  })().toLowerCase();

  return INVALID_REFRESH_TOKEN_MARKERS.some(marker => text.includes(marker));
};

/**
 * Hook to integrate API service with OIDC authentication
 * Sets up automatic token management for API calls
 */
let inFlightSilentRenew: Promise<User | null> | null = null;

export const useApiIntegration = () => {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;

  useEffect(() => {
    // Set up token provider for API service
    const getAccessToken = async (): Promise<string | null> => {
      const current = authRef.current;
      if (current.user) {
        // Choose which token to send based on configuration
        const source = ENV.API_TOKEN_SOURCE;
        const selectedToken = source === 'id' ? current.user.id_token : current.user.access_token;
        if (!selectedToken) {
          return null;
        }
        // Check if token is about to expire and refresh if needed
        if (current.user.expires_at && current.user.expires_at * 1000 < Date.now() + 60000) {
          try {
            inFlightSilentRenew ??= current.signinSilent().finally(() => { inFlightSilentRenew = null; });
            const renewed = await inFlightSilentRenew;
            const renewedToken = source === 'id' ? renewed?.id_token : renewed?.access_token;
            return renewedToken ?? selectedToken;
          } catch (error) {
            console.info('Silent token refresh failed:', error);
            if (isInvalidRefreshTokenError(error)) {
              console.info('[Auth] Silent renew rejected refresh token. Redirecting to login.');
              try {
                await current.removeUser();
              } catch (removeError) {
                console.info('[Auth] Failed to remove stale OIDC user after silent renew failure:', removeError);
              }
              try {
                localStorage.removeItem(`oidc.user:${ENV.OIDC_AUTHORITY}:${ENV.OIDC_CLIENT_ID}`);
                sessionStorage.clear();
              } catch (storageError) {
                console.info('[Auth] Failed to clear storage after silent renew failure:', storageError);
              }
              globalThis.location.href = '/login';
              return null;
            }
            return selectedToken; // Return current token even if refresh failed
          }
        }
  return selectedToken ?? null;
      }
      return null;
    };

    // Configure API service with token provider
  apiService.setTokenProvider(getAccessToken);
  // Also wire the new modular http client so domain services receive tokens
  setHttpTokenProvider(getAccessToken);
  // Also make the apiClient aware of token provider so services importing apiClient directly work
  apiClient.setTokenProvider(getAccessToken);
  }, []);

  return {
    // Expose useful auth and API state
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user,
    error: auth.error,
    apiService,
  };
};